// Round 36×36 icon button. Used in the top bar (back arrow, hamburger, save).
// Keep the aria-label mandatory — these have no visible text.
//
// Ported from handoff_input/design/screens/shared.jsx :: ICSIconBtn.

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> {
  /** Required — these buttons carry only an icon, so screen readers depend
   *  on the label. The prototype also enforced this. */
  ariaLabel: string;
  children: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton({ ariaLabel, children, className, type, ...rest }, ref) {
    return (
      <button
        ref={ref}
        type={type ?? "button"}
        aria-label={ariaLabel}
        className={cn(
          "flex h-9 w-9 items-center justify-center",
          "rounded-full bg-transparent text-ink-muted",
          "transition-colors hover:bg-paper-muted hover:text-ink",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rame focus-visible:ring-offset-2 focus-visible:ring-offset-paper",
          className,
        )}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
